import * as lodash from 'lodash';
import { OrderingHooks, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdAlert } from 'react-icons/io';
import { RiArrowDropDownLine, RiArrowDropUpLine } from 'react-icons/ri';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import Text from '../components/Text';
import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import { TEXT_PROPERTIES } from '../utils/theme';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { PURCHASE_EDITOR_MODAL_ID } from '../modals/PurchaseEditorModalV2';
import {
  logEvent,
  removeFromCart,
  addToCart,
  viewProductDetailPage,
  updateCart,
} from '../utils/analytics';
import EVENTS from '../constants/events';
import QuantityControl from './QuantityControl';
import StandardButton from './StandardButton';
import TouchableOpacity from './TouchableOpacity';
import getCart from '../libs/polygon-ordering/src/selectors/getCart';

const { useFormattedCurrency } = OrderingHooks;

const {
  updateSelectedPaymentMethods,
  adjustStagedPurchaseQuantity,
  removeStagedPurchase,
  openStagedPurchase,
} = OrderingOperations;

const {
  getCartContainsInvalidQuantities,
  getNestedItemStockBalancesData,
  getStagedPurchaseAvailability,
  getItemAvailabilityById,
  getBrands,
  getRootCategory,
  getMember,
  getSelectedOffer,
  getMenu,
  getLocation,
  getSaleType,
} = OrderingSelectors;

const getItemAlreadyInCart = (
  cartItem: CartItem,
  item: Item,
  choicesWithQuantity: ChoiceWithQuantity[],
) => {
  const productId = item.default_size || item.id;
  if (cartItem.ProductID.toString() !== productId) return false;
  if (cartItem.Ingredients.length !== choicesWithQuantity.length) return false;

  const map = new Map();
  for (const { ProductID, Qty } of cartItem.Ingredients) {
    map.set(ProductID, (map.get(ProductID) || 0) + Qty);
  }

  for (const { id, quantity } of choicesWithQuantity) {
    if (map.get(Number(id)) !== quantity) return false;
    map.delete(Number(id));
  }

  return map.size === 0;
};

const removeFromCartLogging = ({
  purchase,
  cart,
  item,
  member,
  selectedOffer,
  menu,
  brand,
  category,
  store,
  saleType,
}: {
  purchase?: PurchaseWithTotals;
  cart: CartItem[];
  item: Item;
  member?: Member;
  selectedOffer?: Offer;
  menu?: Menu;
  brand: string | null;
  category: string | null;
  store?: string;
  saleType: SALE_TYPE;
}) => {
  if (purchase) {
    removeFromCart({
      product: item,
      quantity: purchase.quantity,
      member,
      choicesWithQuantity: purchase.choicesWithQuantity,
      moneyPrice: purchase.moneyPrice,
      brand,
      category,
      store,
      saleType,
    });

    // The cart, as passed to updateCart, must have the item removed
    const itemIndex = cart.findIndex(cartItem => {
      return getItemAlreadyInCart(cartItem, item, purchase.choicesWithQuantity);
    });
    cart.splice(itemIndex, 1);

    updateCart({ member, cart, selectedOffer, menu });
  }
};

const increaseQtyLogging = ({
  purchase,
  category,
  cart,
  item,
  member,
  brand,
  selectedOffer,
  menu,
  location,
  saleType,
}: {
  purchase: PurchaseWithTotals;
  category: string | null;
  cart: CartItem[];
  item: Item;
  member?: Member;
  brand: string | null;
  selectedOffer?: Offer;
  menu?: Menu;
  location?: POSLocation;
  saleType: SALE_TYPE;
}) => {
  addToCart({
    product: item,
    moneyPrice: purchase.moneyPrice,
    quantity: 1, // Can only add one item at a time via the quantity picker.
    choicesWithQuantity: purchase.choicesWithQuantity,
    brand,
    category: category || null,
    member,
    store: location?.name,
    saleType,
    origin: 'Cart Section',
  });

  /**
   * adjustStagedPurchaseQuantity cannot be awaited, but we need to log the updated
   * cart value to updateCart. Will need to check if the item already exists in the
   * cart and update the quantity, and if it doesn't exist it needs to be added.
   */
  const itemAlreadyInCart = cart.find(cartItem => {
    return getItemAlreadyInCart(cartItem, item, purchase.choicesWithQuantity);
  });
  if (itemAlreadyInCart) itemAlreadyInCart.Qty = ++itemAlreadyInCart.Qty;

  updateCart({ member, cart, selectedOffer, menu });
};

const decreaseQtyLogging = ({
  purchase,
  cart,
  item,
  member,
  selectedOffer,
  menu,
  brand,
  category,
  store,
  saleType,
}: {
  purchase: PurchaseWithTotals;
  cart: CartItem[];
  item: Item;
  member?: Member;
  selectedOffer?: Offer;
  menu?: Menu;
  brand: string | null;
  category: string | null;
  store?: string;
  saleType: SALE_TYPE;
}) => {
  removeFromCart({
    product: item,
    quantity: 1, // Can only remove one item at a time via the quantity picker.
    member,
    choicesWithQuantity: purchase.choicesWithQuantity,
    moneyPrice: purchase.moneyPrice,
    brand,
    category,
    store,
    saleType,
  });
  /**
   * adjustStagedPurchaseQuantity cannot be awaited, but we need to log the updated
   * cart value to updateCart. Will need to check if the item's quantity is greater
   * than one and decrement it. If it's exactly one the item needs to be removed.
   */
  const itemAlreadyInCart = cart.find(cartItem => {
    return getItemAlreadyInCart(cartItem, item, purchase.choicesWithQuantity);
  });
  if (itemAlreadyInCart?.Qty && itemAlreadyInCart.Qty > 1) {
    itemAlreadyInCart.Qty = --itemAlreadyInCart.Qty;
  } else {
    cart.splice(
      cart.findIndex(cartItem => itemAlreadyInCart?.ProductID === cartItem.ProductID),
      1,
    );
  }
  updateCart({ member, cart, selectedOffer, menu });
};

// flatten the choices out of the choicesets in a nested item
const flattenChoiceSets = (
  choiceSets: ChoiceSetWithQuantity[],
): ChoiceWithQuantity[] | undefined => {
  return choiceSets?.reduce((flattened, choiceSet) => {
    flattened.push(...choiceSet.choices.filter(c => c && !!c.quantity));

    return flattened;
  }, [] as ChoiceWithQuantity[]);
};

const SubLine = ({
  quantity = 0,
  label,
  amount,
  item,
  cartHasInvalidItems,
  qtyUpdates,
  marginStyles,
  stockBalanceData,
}: {
  quantity?: number;
  label?: string;
  amount?: number;
  item?: Item | Choice | NestedChoice;
  cartHasInvalidItems?: any;
  qtyUpdates?: any;
  marginStyles?: boolean | false;
  stockBalanceData?: StockBalanceData;
}) => {
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const dollarAmount = useFormattedCurrency({ cents: Number(amount) });
  const itemPLU = item?.plucode;
  const itemId = item?.id;
  const availability = useAppSelector(getItemAvailabilityById(itemId));
  const soldOut = stockBalanceData?.soldOut;
  return (
    <>
      <div style={styles.subLine}>
        <div style={styles.left}>
          <div
            style={combineStyles(styles.subLineLeftContent, marginStyles && styles.choiceSetPush)}
          >
            <Text themeKey="stagedPurchaseSubLine">
              {`${quantity > 1 ? `${quantity} × ` : ''}${label}`}
              {soldOut ||
                (!availability && (
                  <div style={styles.badgeAndMessageContainer}>
                    <IoMdAlert
                      style={combineStyles(
                        p('cartItemErrorAlert', ['color']),
                        styles.cartItemErrorAlert,
                      )}
                    />
                    <Text
                      themeKey="stagedPurchaseSubLine"
                      style={combineStyles(p('cartItemError', ['color']), styles.cartItemError)}
                    >
                      {t('itemOutOfStockMessage')}
                    </Text>
                  </div>
                ))}

              {itemPLU && itemPLU in (qtyUpdates || {}) && cartHasInvalidItems && !soldOut && (
                <div style={styles.badgeAndMessageContainer}>
                  <IoMdAlert
                    style={combineStyles(
                      p('cartItemWarningAlert', ['color']),
                      styles.cartItemWarningAlert,
                    )}
                  />
                  <Text
                    themeKey="stagedPurchaseSubLine"
                    style={combineStyles(p('cartItemWarning', ['color']), styles.cartItemWarning)}
                  >
                    {t('itemQuantityWarningMessage', {
                      newItemQuantity: qtyUpdates[itemPLU!],
                      someLabel: label,
                    })}
                  </Text>
                </div>
              )}
            </Text>
          </div>
        </div>

        {amount != null && amount !== 0 && (
          <Text themeKey="stagedPurchaseSubLine">{dollarAmount}</Text>
        )}
      </div>
      <div style={styles.choiceSetPush}>
        {/* item choicesets / ingredients */}
        {'choiceSets' in (item || {}) &&
          flattenChoiceSets((item as NestedChoiceWithQuantity).choiceSets)?.map(
            (choice: any, index: number) => {
              return (
                <SubLine
                  key={index}
                  label={choice.name}
                  quantity={choice.quantity}
                  amount={choice.moneyPrice}
                  item={choice}
                  //@ts-ignore
                  cartHasInvalidItems={cartHasInvalidItems?.cartContainsInvalidQuantities}
                  // This can be a safe guard for the CompleteOrderScreen Bug qtyUpdates={qtyUpdates ? qtyUpdates : {}}
                  qtyUpdates={qtyUpdates}
                  marginStyles
                  stockBalanceData={stockBalanceData}
                />
              );
            },
          )}
      </div>
    </>
  );
};

type IProps = {
  readOnly?: boolean;
  purchase?: PurchaseWithTotals;
  unavailablePurchase?: _Purchase;
  hideQuantityWhenSingular?: boolean;
  qtyUpdates: any;
};

const StagedPurchase: React.FC<IProps> = ({
  purchase,
  unavailablePurchase,
  readOnly,
  hideQuantityWhenSingular,
  qtyUpdates,
}) => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const hideQuantity = purchase && hideQuantityWhenSingular && purchase.quantity === 1;
  const dispatch = useAppDispatch();
  const cartHasInvalidItems = useAppSelector(getCartContainsInvalidQuantities);
  const brands = useAppSelector(getBrands);
  const rootCategory = useAppSelector(getRootCategory);
  const member = useAppSelector(getMember);
  const cart = useAppSelector(getCart);
  const selectedOffer = useAppSelector(getSelectedOffer);
  const menu = useAppSelector(getMenu);
  const location = useAppSelector(getLocation);
  const saleType = useAppSelector(getSaleType);

  const stagedPurchaseAvailability = useAppSelector(getStagedPurchaseAvailability(purchase));

  const isNestedItem = purchase?.choiceSets?.reduce(
    (acc, set) =>
      acc ||
      (lodash.has(set, 'nestedIngredients') && (set as ValidatedNestedChoiceSet).nestedIngredients),
    false,
  );
  const [stockBalanceDataMap, minBalanceStaged] = useAppSelector(
    getNestedItemStockBalancesData(
      purchase,
      isNestedItem ? (purchase?.choiceSelections as NestedChoiceSelections) : undefined,
    ),
  );
  const { item } = (purchase || unavailablePurchase) as PurchaseWithTotals;
  const stockBalanceData = (stockBalanceDataMap &&
    item?.plucode &&
    stockBalanceDataMap[item?.plucode]) as StockBalanceData | undefined;

  const [showDetails, setShowDetails] = useState(false);

  const sizeItem = purchase?.item.sizes.find(s => s.id === purchase.sizeId);

  const discountMoneyPrice = useFormattedCurrency({ cents: purchase?.discountedMoneyPrice });

  const categories = rootCategory?.subCategories || [];

  const brand = brands.find(brand => brand.id === purchase?.brandId)?.name || null;
  const category =
    categories.find(category =>
      lodash.map(category.items, 'id').find(id => id === purchase?.item.id),
    )?.name || null;

  const unavailableItemBasePrice = useFormattedCurrency({
    cents: unavailablePurchase?.item?.baseMoneyPrice,
  });

  const renderQuantitySection = () => {
    if (hideQuantity) {
      return null;
    }

    if (readOnly) {
      return (
        <Text themeKey="stagedPurchase">
          {purchase?.quantity ?? unavailablePurchase?.quantity}&nbsp;
        </Text>
      );
    }

    if (purchase || unavailablePurchase) {
      return (
        <QuantityControl
          quantity={(purchase?.quantity ?? unavailablePurchase?.quantity)!}
          maxQuantity={minBalanceStaged}
          iconContainerStyle={{ fontSize: 20, color: 'red' }}
          textStyle={{ fontSize: 16 }}
          buttonSize={22}
          increaseQty={() => {
            if (!purchase) return;
            dispatch(
              adjustStagedPurchaseQuantity({
                purchaseId: purchase.id,
                adjustment: 0,
                newQuantity: Number(purchase.quantity + 1),
              }),
            );
            increaseQtyLogging({
              purchase,
              category,
              cart,
              item: item.sizes.find(size => size.id === purchase.sizeId) || item,
              member,
              brand,
              selectedOffer,
              menu,
              location,
              saleType,
            });
          }}
          decreaseQty={() => {
            if (!purchase) return;
            dispatch(
              adjustStagedPurchaseQuantity({
                purchaseId: purchase.id,
                adjustment: 0,
                newQuantity: Number(purchase.quantity - 1),
              }),
            );
            decreaseQtyLogging({
              purchase,
              cart,
              item: item.sizes.find(size => size.id === purchase.sizeId) || item,
              member,
              selectedOffer,
              menu,
              brand,
              store: location?.name,
              category,
              saleType,
            });
          }}
          increaseEnabled={!unavailablePurchase}
          decreaseEnabled={!unavailablePurchase}
        />
      );
    }

    return null;
  };

  return (
    <div style={styles.mainContainer}>
      <div style={styles.top}>
        <div style={styles.left}>
          <div>
            <Text
              themeKey="stagedPurchase"
              style={combineStyles(
                { overflowWrap: 'anywhere' },
                unavailablePurchase && p('unavailablePurchaseText', ['opacity']),
              )}
            >
              {purchase?.item.name ?? unavailablePurchase?.item?.name}
            </Text>
            {!stagedPurchaseAvailability && (
              <div>
                <IoMdAlert
                  style={combineStyles(p('cartItemError', ['color']), styles.cartItemErrorAlert)}
                />
                <Text
                  themeKey="stagedPurchaseSubLine"
                  style={combineStyles(p('cartItemError', ['color']), styles.cartItemWarning)}
                >
                  {t('itemUnavailable')}
                </Text>
              </div>
            )}
          </div>
          {((purchase?.choicesWithQuantity && purchase?.choicesWithQuantity.length > 0) ||
            (purchase?.moneyDiscount || 0) > 0 ||
            sizeItem) && (
            <TouchableOpacity
              style={combineStyles(p('cartShowDetails', ['color']), {
                fontSize: 12,
                opacity: 0.7,
              })}
              onClick={() => setShowDetails(preState => !preState)}
            >
              {t('showItemDetails')}
              {showDetails ? (
                <RiArrowDropUpLine size={20} color={p('cartShowDetails', ['color']).color} />
              ) : (
                <RiArrowDropDownLine size={20} color={p('cartShowDetails', ['color']).color} />
              )}
            </TouchableOpacity>
          )}

          {showDetails && (
            <div style={styles.subLines}>
              {/* size option */}
              {!!sizeItem && (
                <SubLine
                  label={sizeItem.name}
                  amount={
                    sizeItem.baseMoneyPrice -
                    (purchase?.item.minSizeBaseMoneyPrice ?? purchase?.baseMoneyPrice ?? 0)
                  }
                  item={sizeItem}
                />
              )}
              {/* item parent */}
              {(purchase?.choicesWithQuantity || []).map((choice: any, index) => (
                <React.Fragment key={index}>
                  <SubLine
                    label={choice.name}
                    quantity={choice.quantity}
                    amount={choice.moneyPrice}
                    item={choice}
                    //@ts-ignore
                    cartHasInvalidItems={cartHasInvalidItems.cartContainsInvalidQuantities}
                    // This can be a safe guard for the CompleteOrderScreen Bug qtyUpdates={qtyUpdates ? qtyUpdates : {}}
                    qtyUpdates={qtyUpdates}
                  />
                </React.Fragment>
              ))}

              {!!purchase?.moneyDiscount && (
                <SubLine label={t('stagedPurchaseDiscount')} amount={purchase.moneyDiscount * -1} />
              )}
            </div>
          )}

          <div style={styles.buttons}>
            {!purchase?.item.simpleItem && !stockBalanceData?.soldOut && !unavailablePurchase && (
              <StandardButton
                label={t('button.stagedPurchase.customise')}
                onClick={() =>
                  purchase &&
                  dispatch(openStagedPurchase({ purchaseId: purchase.id })).then(() => {
                    dispatch(
                      setCurrentModal({
                        modalId: PURCHASE_EDITOR_MODAL_ID,
                        preserveReturnModalDetails: true,
                      }),
                    );

                    logEvent(EVENTS.OPEN_EXISTING_PURCHASE);
                    viewProductDetailPage({
                      product: purchase.item,
                      quantity: purchase.quantity,
                      choicesWithQuantity: purchase.choicesWithQuantity,
                      brand,
                      category,
                      member,
                      store: location?.name,
                      saleType,
                    });
                  })
                }
                // LeftIconComponent={RiPencilFill}
                themeKey="stagedPurchaseButton"
                containerStyle={styles.button}
                // leftIconStyle={iconStyle}
                invisibleContainer
                labelStyle={p('stagedPurchaseCustomiseButtonText', TEXT_PROPERTIES)}
              />
            )}

            {unavailablePurchase && (
              <Text
                themeKey="stagedPurchaseButton"
                value={t('button.stagedPurchase.unavailable')}
                style={combineStyles(styles.button, p('unavailablePurchaseText', ['opacity']))}
              />
            )}

            <StandardButton
              label={t('button.stagedPurchase.remove')}
              onClick={() => {
                const id = purchase?.id ?? unavailablePurchase?.id;
                if (!id) return;
                dispatch(removeStagedPurchase(id));
                dispatch(updateSelectedPaymentMethods({}));

                logEvent(EVENTS.REMOVE_PURCHASE);
                removeFromCartLogging({
                  purchase,
                  cart,
                  item: item.sizes.find(size => size.id === purchase?.sizeId) || item,
                  member,
                  selectedOffer,
                  menu,
                  brand,
                  category,
                  store: location?.name,
                  saleType,
                });
              }}
              // LeftIconComponent={RiCloseCircleFill}
              themeKey="stagedPurchaseButton"
              labelStyle={p('stagedPurchaseRemoveButtonText', TEXT_PROPERTIES)}
              containerStyle={styles.button}
              // leftIconStyle={iconStyle}
              invisibleContainer
            />
          </div>
        </div>

        <div style={styles.right}>
          <Text
            themeKey="stagedPurchase"
            style={unavailablePurchase && combineStyles(p('unavailablePurchaseText', ['opacity']))}
          >
            {(purchase && discountMoneyPrice) ??
              (unavailablePurchase?.item &&
                unavailablePurchase?.item?.baseMoneyPrice &&
                `From ${unavailableItemBasePrice}`)}
          </Text>
          {renderQuantitySection()}
        </div>
      </div>

      {!readOnly && purchase && (
        <div>
          {stockBalanceData?.soldOut && (
            <div style={styles.badgeAndMessageContainer}>
              <IoMdAlert
                style={combineStyles(p('cartItemErrorAlert', ['color']), styles.cartItemErrorAlert)}
              />
              <Text
                themeKey="stagedPurchaseSubLine"
                style={combineStyles(p('cartItemError', ['color']), styles.cartItemError)}
              >
                {t('itemOutOfStockMessage')}
              </Text>
            </div>
          )}
          {purchase.item.id in (qtyUpdates || {}) &&
            purchase.choicesWithQuantity.length === 0 &&
            cartHasInvalidItems.cartContainsInvalidQuantities &&
            !stockBalanceData?.soldOut && (
              <div style={styles.badgeAndMessageContainer}>
                <IoMdAlert
                  style={combineStyles(
                    p('cartItemWarningAlert', ['color']),
                    styles.cartItemWarningAlert,
                  )}
                />
                <Text
                  themeKey="stagedPurchaseSubLine"
                  style={combineStyles(p('cartItemWarning', ['color']), styles.cartItemWarning)}
                >
                  {t('itemQuantityWarningMessage', {
                    newItemQuantity: qtyUpdates[purchase.item.id],
                    someLabel: purchase.item.name,
                  })}
                </Text>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

const styles: Styles = {
  mainContainer: {
    //marginBottom: 20,
  },

  top: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretched',
  },

  left: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  /* middle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  }, */
  right: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  subLines: {
    paddingLeft: 5,
    paddingTop: 10,
  },

  subLine: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
    gap: 10,
  },
  subLineLeftContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  choiceSetPush: {
    margin: '0px 0px 0px 12px',
  },

  quantitySelect: {
    fontFamily: 'Roboto',
    marginRight: 7,
    // marginBottom: 5,
  },

  buttons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingLeft: 0,
    paddingTop: '0.5em',
  },
  button: {
    marginRight: 20,
  },
  adjust: {
    marginLeft: 30,
  },
  cartItemError: {
    paddingLeft: 5,
  },
  cartItemErrorAlert: {
    fontSize: 20,
  },
  cartItemWarning: {
    paddingLeft: 5,
  },
  cartItemWarningAlert: {
    fontSize: 20,
  },

  badgeAndMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginLeft: -2,
  },
  spanCartSublineMessage: {
    marginBottom: -5,
    marginLeft: 5,
  },
};

export default StagedPurchase;
